import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { apiErrorMessage } from '../lib/apiError';
import {
  AlertIcon,
  ArrowRightIcon,
  BoxIcon,
  ChartIcon,
  EyeIcon,
  InvoiceIcon,
  ShieldIcon,
  SparkIcon,
} from '../components/icons';

const LAST_IDENTIFIER_KEY = 'gravenca.last-identifier';
const FEATURES = [
  {
    icon: InvoiceIcon,
    title: 'Facturación y fiscalidad',
    detail: 'TFHKA, notas de crédito/débito y retenciones',
  },
  {
    icon: BoxIcon,
    title: 'Inventario y compras',
    detail: 'Stock, costos y proveedores en tiempo real',
  },
  {
    icon: ChartIcon,
    title: 'Cobranzas y tesorería',
    detail: 'Bancos, cartera y resultados en bolívares',
  },
] as const;

function readLastIdentifier(): string {
  try {
    return window.localStorage.getItem(LAST_IDENTIFIER_KEY) ?? '';
  } catch {
    return '';
  }
}

export function LoginPage() {
  const { user, status, login } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const [identifier, setIdentifier] = useState(readLastIdentifier);
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (status === 'ready' && user) return <Navigate to={from} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(identifier.trim(), password);
      try {
        if (remember) window.localStorage.setItem(LAST_IDENTIFIER_KEY, identifier.trim());
        else window.localStorage.removeItem(LAST_IDENTIFIER_KEY);
      } catch {
        // El navegador puede bloquear localStorage; el login ya fue exitoso.
      }
    } catch (loginError) {
      setError(
        apiErrorMessage(
          loginError,
          'No se pudo iniciar sesión. Verifica tus credenciales e inténtalo de nuevo.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const updateCapsLock = (event: KeyboardEvent<HTMLInputElement>) => {
    setCapsLock(event.getModifierState?.('CapsLock') ?? false);
  };

  return (
    <div className="login-page">
      <aside className="login-brand">
        <div className="login-brand__top">
          <span className="login-brand__badge">
            <span className="login-brand__badge-dot" />
            GRAVENCA · ERP 2026
          </span>
          <span className="login-brand__secure">
            <ShieldIcon size={14} />
            Acceso restringido
          </span>
        </div>

        <div className="login-brand__content">
          <div className="login-brand__hero">
            <img
              src="/brand/isotipo-white.png"
              alt="GRAVENCA"
              className="login-brand__logo login-brand__logo--hero"
            />
            <img
              src="/brand/wordmark-white.png"
              alt="GRAVENCA"
              className="login-brand__wordmark login-brand__wordmark--hero"
            />
          </div>

          <h1 className="login-brand__title">
            La operación de GRAVENCA,
            <span> en un solo lugar.</span>
          </h1>
          <p className="login-brand__lead">
            Del pedido a la factura fiscal, del inventario a la cobranza. Todo conectado para que
            tu equipo decida con información real.
          </p>

          <ul className="login-features">
            {FEATURES.map(({ icon: Icon, title, detail }) => (
              <li key={title}>
                <span className="login-features__icon">
                  <Icon size={18} />
                </span>
                <span>
                  <strong>{title}</strong>
                  <small>{detail}</small>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <footer className="login-brand__footer">
          <span>GRAVENCA, C.A.</span>
          <span>RIF J-508362438</span>
          <span>Sistema de gestión empresarial</span>
        </footer>
      </aside>

      <main className="login-panel">
        <div className="login-shell">
          <div className="login-card">
            <div className="login-card__head">
              <span className="pill pill--soft login-card__eyebrow">
                <SparkIcon size={13} />
                Bienvenido de nuevo
              </span>
              <h2>Inicia sesión</h2>
              <p>Entra con tu correo corporativo o con tu código de usuario.</p>
            </div>

            <form
              onSubmit={(event) => void submit(event)}
              className="login-form"
              aria-busy={loading}
            >
              <label className="field">
                <span className="field-label">Correo o código de usuario</span>
                <input
                  className="input"
                  type="text"
                  inputMode="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={254}
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  placeholder="nombre@gravenca.com o V-02"
                  aria-describedby="login-identifier-help"
                  autoFocus
                  required
                />
                <span className="field-help" id="login-identifier-help">
                  También puedes entrar con tu código, por ejemplo <strong>admin02</strong>.
                </span>
              </label>

              <label className="field">
                <span className="field-label">Contraseña</span>
                <div className="password-field login-password">
                  <input
                    className="input"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    onKeyDown={updateCapsLock}
                    onKeyUp={updateCapsLock}
                    placeholder="Escribe tu contraseña"
                    required
                  />
                  <button
                    type="button"
                    className="icon-button icon-button--tiny"
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    <EyeIcon size={17} />
                  </button>
                </div>
                {capsLock ? (
                  <span className="login-caps" role="status">
                    <AlertIcon size={13} />
                    Bloq Mayús está activado
                  </span>
                ) : null}
              </label>

              {error ? (
                <div className="form-error login-error" role="alert">
                  <AlertIcon size={16} />
                  <span>{error}</span>
                </div>
              ) : null}

              <div className="login-options">
                <label className="login-remember">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(event) => setRemember(event.target.checked)}
                  />
                  Recordar mi usuario
                </label>
                <span className="login-help">¿Olvidaste tu contraseña? Contacta al administrador.</span>
              </div>

              <button
                type="submit"
                className="btn btn--primary btn--block login-submit"
                disabled={loading}
                aria-busy={loading}
              >
                <span>{loading ? 'Verificando…' : 'Entrar al ERP'}</span>
                <ArrowRightIcon size={17} />
              </button>
            </form>

            <div className="login-trust">
              <ShieldIcon size={15} />
              <span>Sesión protegida con cifrado y control de accesos por rol.</span>
            </div>

            <p className="login-legal">
              Al continuar aceptas las políticas de uso y confidencialidad de GRAVENCA.
            </p>
          </div>

          <div className="login-panel__meta">
            <span>GRAVENCA ERP · v0.2</span>
            <span>Hecho para la operación real</span>
          </div>
        </div>
      </main>
    </div>
  );
}
